import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { Eye, EyeOff, Loader2 } from "lucide-react";

/**
 * Admin view of the automatic guide writer: what it has published, what is
 * queued, and whether it has stopped itself.
 */

interface EngineState {
  paused_reason: string | null;
  pages_this_week: number;
  weekly_page_cap: number;
  week_start: string;
  last_run_at: string | null;
  last_run_note: string | null;
}

interface PageRow {
  id: string;
  slug: string;
  title: string;
  kind: string;
  status: string;
  published_at: string;
}

interface JobRow {
  id: string;
  kind: string;
  target_key: string;
  status: string;
  attempts: number;
  last_error: string | null;
}

export const ContentEnginePanel = () => {
  const [state, setState] = useState<EngineState | null>(null);
  const [pages, setPages] = useState<PageRow[]>([]);
  const [jobs, setJobs] = useState<JobRow[]>([]);
  const [busy, setBusy] = useState(true);

  const load = useCallback(async () => {
    setBusy(true);
    const [stateRes, pagesRes, jobsRes] = await Promise.all([
      supabase.from("content_engine_state").select("*").eq("id", 1).maybeSingle(),
      supabase
        .from("content_pages")
        .select("id, slug, title, kind, status, published_at")
        .order("published_at", { ascending: false })
        .limit(25),
      supabase
        .from("content_jobs")
        .select("id, kind, target_key, status, attempts, last_error")
        .neq("status", "done")
        .order("updated_at", { ascending: false })
        .limit(15),
    ]);
    setState((stateRes.data as EngineState) ?? null);
    setPages((pagesRes.data as PageRow[]) ?? []);
    setJobs((jobsRes.data as JobRow[]) ?? []);
    setBusy(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = async (page: PageRow) => {
    const next = page.status === "published" ? "hidden" : "published";
    await supabase
      .from("content_pages")
      .update({ status: next, updated_at: new Date().toISOString() })
      .eq("id", page.id);
    void load();
  };

  return (
    <Card className="rounded-xl shadow-md">
      <CardContent className="py-6 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-xl font-bold text-foreground">Automatic guides</h2>
          <Button
            variant="outline"
            onClick={() => void load()}
            className="min-h-[44px] rounded-xl active:scale-95"
          >
            Refresh
          </Button>
        </div>

        {busy && <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />}

        {state && (
          <div className="space-y-1 text-base">
            <p className="text-muted-foreground">
              {state.pages_this_week} of {state.weekly_page_cap} pages written this week (week of{" "}
              {state.week_start}).
            </p>
            {state.last_run_at && (
              <p className="text-muted-foreground">
                Last run {new Date(state.last_run_at).toLocaleString()} — {state.last_run_note}
              </p>
            )}
            {state.paused_reason && (
              <p className="font-medium text-destructive">Paused: {state.paused_reason}</p>
            )}
          </div>
        )}

        <div className="space-y-2">
          <h3 className="font-semibold text-foreground">Published</h3>
          {pages.length === 0 ? (
            <p className="text-base text-muted-foreground">Nothing yet.</p>
          ) : (
            pages.map((page) => (
              <div key={page.id} className="flex items-center justify-between gap-3 text-base">
                <Link
                  to={`/guides/${page.slug}`}
                  className="truncate text-foreground hover:text-primary"
                >
                  {page.title}
                </Link>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => void toggle(page)}
                  className="min-h-[44px] shrink-0 rounded-xl active:scale-95"
                  aria-label={page.status === "published" ? "Hide this guide" : "Show this guide"}
                >
                  {page.status === "published" ? (
                    <Eye className="h-4 w-4" />
                  ) : (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </div>
            ))
          )}
        </div>

        <div className="space-y-2">
          <h3 className="font-semibold text-foreground">Queue</h3>
          {jobs.length === 0 ? (
            <p className="text-base text-muted-foreground">Nothing waiting.</p>
          ) : (
            jobs.map((job) => (
              <div key={job.id} className="flex items-center justify-between gap-3 text-base">
                <span className="truncate text-muted-foreground">
                  {job.kind} · {job.target_key}
                </span>
                <span className="shrink-0 text-foreground">
                  {job.status}
                  {job.attempts > 0 ? ` (${job.attempts})` : ""}
                </span>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
};
